import React, { useMemo } from 'react';
import { isErrorDialogOpenAtom } from '@smart-tv/atoms';
import { setFocus, useFocusable } from '@noriginmedia/norigin-spatial-navigation';

import { usePlayerContext } from '../../_contexts/player-context';
import useAutoHide from '../../_hooks/use-auto-hide';
import usePlayerEvent from '../../_hooks/use-player-event';
import {
  AUTO_HIDE_TIMEOUT,
  PLAYER_FOCUS_KEY_INFO,
  PLAYER_FOCUS_KEY_ROOT,
  PLAYER_FOCUS_KEY_TOGGLE_PLAY
} from '../../player-helpers';
import { AudioTrack, PlayerEvent, SubtitleTrack } from '../../types';
import { PlayerControlsProps } from '../player-controls';
import { isSkippingAtom } from '../player-controls-navigation/skip-buttons';
import { atom, useAtomValue } from 'jotai';
import useStream from '../../_hooks/use-stream';
import { channelsInteractionAtom, descalePlayerAtom, playerWaitingAtom } from '../../atoms';
import cleanAudioTrackText from '@smart-tv/utils/formatter/clean-track-text';
import {
  retrievePersistentData,
  storePersistentData
} from '@smart-tv/utils/persistent-data/persistent-data';
import { isBackKey } from '@smart-tv/utils/remoteKeys';
import { playerControlsOpenAtom } from '../../player-content/use-player-content';

export const autoHideTimeoutExtensionAtom = atom(0);

const DANISH_AUDIO_TRACK_LANG = 'dan';

const usePlayerControls = (props: PlayerControlsProps) => {
  const { open, onClose } = props;

  const { stream } = useStream();

  const [audioTracks, setAudioTracks] = React.useState<AudioTrack[]>();
  const [selectedAudioTrack, setSelectedAudioTrack] = React.useState<AudioTrack>();
  const [subtitleTracks, setSubtitleTracks] = React.useState<SubtitleTrack[]>();
  const [selectedSubtitleTrack, setSelectedSubtitleTrack] = React.useState<SubtitleTrack>();

  const isSkipping = useAtomValue(isSkippingAtom);
  const autoHideTimeoutExtensionValue = useAtomValue(autoHideTimeoutExtensionAtom);
  const descalePlayer = useAtomValue(descalePlayerAtom);
  const channelsInteraction = useAtomValue(channelsInteractionAtom);
  const playerWaiting = useAtomValue(playerWaitingAtom);

  const calculatedAutoHideTimeout = useMemo(() => {
    return descalePlayer ? 0 : autoHideTimeoutExtensionValue + AUTO_HIDE_TIMEOUT;
  }, [autoHideTimeoutExtensionValue, descalePlayer]);

  const { player, settingsOverlay, toggleSettingsOverlay, playerRootElem } = usePlayerContext();
  const errorDialogOpen = useAtomValue(isErrorDialogOpenAtom);
  const controlsOpen = useAtomValue(playerControlsOpenAtom);

  const { ref, focusKey, hasFocusedChild } = useFocusable({
    focusable: controlsOpen,
    isFocusBoundary: true,
    trackChildren: true
  });

  /**
   * Focus both the browser focus and the navigation focus, when the controls are opened.
   */
  React.useEffect(() => {
    if (open) {
      stream?.trickmodes ? setFocus(PLAYER_FOCUS_KEY_TOGGLE_PLAY) : setFocus(PLAYER_FOCUS_KEY_INFO);
      ref?.current?.focus();
    }
  }, [open, ref, stream?.trickmodes]);

  React.useEffect(() => {
    if (!controlsOpen && !errorDialogOpen && !settingsOverlay && hasFocusedChild) {
      setFocus(PLAYER_FOCUS_KEY_ROOT);
      playerRootElem?.focus();
    }
  }, [controlsOpen, errorDialogOpen, hasFocusedChild, playerRootElem, settingsOverlay]);

  const {
    handleKeyDown: registerKeyDown,
    handleKeyUp,
    handleMouseOver,
    handleMouseLeave,
    cancel
  } = useAutoHide({
    enabled: !!open && !settingsOverlay && !channelsInteraction && !playerWaiting,
    timeout: calculatedAutoHideTimeout,
    onHide: () => onClose?.()
  });

  const handleResetControls = React.useCallback(() => {
    cancel();
    onClose?.();
  }, [cancel, onClose]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      registerKeyDown();

      if (isBackKey(event)) {
        event.stopPropagation();

        onClose?.();
      }
    },
    [onClose, registerKeyDown]
  );

  const getSelectedSubtitleTrack = (subtitles: SubtitleTrack[]) => {
    const storedSubtitleTrack = retrievePersistentData<SubtitleTrack>('subtitle-track');
    const selectedSubtitleTrack =
      storedSubtitleTrack &&
      subtitles.find(
        (subtitle) =>
          subtitle.lang === storedSubtitleTrack?.lang &&
          subtitle.label === storedSubtitleTrack?.label
      );

    if (!selectedSubtitleTrack) return;

    return selectedSubtitleTrack;
  };

  const getSelectedAudioTrack = (audioTracks: AudioTrack[]) => {
    const storedAudioTrack = retrievePersistentData<AudioTrack>('audio-track');
    const selectedAudioTrack =
      storedAudioTrack &&
      audioTracks.find(
        (audioTrack) =>
          audioTrack.lang === storedAudioTrack?.lang &&
          cleanAudioTrackText(audioTrack.id) === cleanAudioTrackText(storedAudioTrack?.id)
      );

    if (selectedAudioTrack) return selectedAudioTrack;

    const danishAudioTrack = audioTracks.find(
      (audioTrack) => audioTrack.lang === DANISH_AUDIO_TRACK_LANG && audioTrack.label === 'Danish'
    );

    if (danishAudioTrack) return danishAudioTrack;

    return audioTracks[0];
  };

  const initSubtitleTracks = React.useCallback(() => {
    if (!player) return;

    const subtitlesTracks = player.getSubtitleTracks();
    const selectedSubtitleTrack = getSelectedSubtitleTrack(subtitlesTracks);

    setSubtitleTracks(subtitlesTracks);
    setSelectedSubtitleTrack(selectedSubtitleTrack);

    player.selectSubtitleTrack(selectedSubtitleTrack?.id || '');
  }, [player]);

  const initAudioTracks = React.useCallback(() => {
    if (!player) return;

    const audioTracks = player.getAudioTracks();
    const selectedAudioTrack = getSelectedAudioTrack(audioTracks);

    setAudioTracks(audioTracks);
    setSelectedAudioTrack(selectedAudioTrack);

    player.selectAudioTrack(selectedAudioTrack?.id);
  }, [player]);

  const handleSelectSubtitleTrack = React.useCallback(
    (selectedSubtitleTrack: SubtitleTrack | undefined) => {
      setSelectedSubtitleTrack(selectedSubtitleTrack);
      storePersistentData('subtitle-track', selectedSubtitleTrack, false);
    },
    []
  );

  const handlePlayerOpened = React.useCallback(() => {
    initSubtitleTracks();
    initAudioTracks();
  }, [initAudioTracks, initSubtitleTracks]);

  usePlayerEvent(PlayerEvent.Opened, handlePlayerOpened);

  return {
    handleKeyDown,
    handleKeyUp,
    handleMouseOver,
    handleMouseLeave,
    handleResetControls,
    handleSelectSubtitleTrack,
    selectedSubtitleTrack,
    subtitleTracks,
    audioTracks,
    selectedAudioTrack,
    toggleSettingsOverlay,
    settingsOverlay,
    focusKey,
    ref,
    setSelectedAudioTrack,
    player,
    isSkipping,
    descalePlayer
  };
};

export default usePlayerControls;
